import { Caption } from '@remotion/captions';
import { TrackType } from '@/features/remotion-editor/editor/state/types';

/** ms 단위 값이 유효하지 않은 경우 0으로 보정한다. */
export const safeMs = (value?: number | null) =>
  typeof value === 'number' && Number.isFinite(value) ? value : 0;

/** 끝 지점이 시작 지점 이하로 내려가는 것을 방지한다. */
export const ensureEndGreaterThanStart = (start: number, end: number) =>
  end > start ? end : start + 1;

/** Caption 객체를 안전한 범위로 복제한다. */
export const cloneCaption = (caption: Caption): Caption => {
  const startMs = safeMs(caption.startMs);
  const endMs = ensureEndGreaterThanStart(startMs, safeMs(caption.endMs));

  return {
    ...caption,
    startMs,
    endMs,
    timestampMs:
      caption.timestampMs == null
        ? Math.round((startMs + endMs) / 2)
        : caption.timestampMs,
  };
};

/** caption 배열을 안전 복제한다. */
export const cloneCaptions = (captions: Caption[]): Caption[] =>
  captions.map((caption) => cloneCaption(caption));

/** caption 배열을 시작 ms 기준으로 정렬한다. */
export const sortCaptionsByStart = (captions: Caption[]) =>
  [...captions].sort((a, b) => safeMs(a.startMs) - safeMs(b.startMs));

/** caption 배열의 전체 범위(start/end)를 계산한다. */
export const getCaptionRange = (captions: Caption[]) => {
  if (!captions.length) return { start: 0, end: 0 };

  let minStart = Number.POSITIVE_INFINITY;
  let maxEnd = 0;

  captions.forEach((caption) => {
    const startMs = safeMs(caption.startMs);
    const endMs = ensureEndGreaterThanStart(startMs, safeMs(caption.endMs));
    if (startMs < minStart) {
      minStart = startMs;
    }
    if (endMs > maxEnd) {
      maxEnd = endMs;
    }
  });

  if (!Number.isFinite(minStart)) {
    minStart = 0;
  }

  return { start: minStart, end: maxEnd };
};

/** Remotion clip에 연결될 pseudo id를 생성한다. */
export const createCaptionClipId = () =>
  Date.now() + Math.floor(Math.random() * 1000);

/**
 * track 배열에서 주어진 index의 items를 갱신한다.
 * @param tracks 전체 track 목록
 * @param trackIndex 수정 대상 track index
 * @param updater items 배열을 갱신하는 콜백
 */
export const updateTrackItems = (
  tracks: TrackType[],
  trackIndex: number,
  updater: (items: string[]) => string[],
): TrackType[] =>
  tracks.map((track, idx) =>
    idx === trackIndex ? { ...track, items: updater(track.items) } : track,
  );

/** 캡션 구간의 중앙값 timestamp를 산출한다. */
export const computeMidpoint = (start: number, end: number) =>
  Math.round((start + end) / 2);
